import { useMemo, useRef, useCallback } from "react";
import { Html, useGLTF } from "@react-three/drei";
import { OverlayCanvas } from "./OverlayCanvas";
import { useBuildingDetailStore } from "@/stores/buildingDetailStore";
import { useTenantQuery } from "@/hooks/useTenantQuery";

interface TopRightOverlayProps {
  /** 건물 ID (입주자 조회용) */
  buildingId?: string | null;
  /** 건물 데이터 */
  buildingData?: {
    title?: string | null;
    description?: string | null;
    ownerName?: string | null;
  };
  /** 퇴거 처리 핸들러 */
  onEvict?: (tenant: any) => void;
}

/**
 * 클립 3D 모델 컴포넌트
 */
function ClipModel() {
  const { scene: clipScene } = useGLTF(
    import.meta.env.BASE_URL + "/models/clip.glb"
  );

  const clonedClip = useMemo(() => {
    if (!clipScene) return null;
    return clipScene.clone();
  }, [clipScene]);

  if (!clonedClip) return null;

  return <primitive object={clonedClip} />;
}

// GLB 프리로드
useGLTF.preload(import.meta.env.BASE_URL + "/models/clip.glb");

/**
 * TopRight 오버레이 컴포넌트
 * ZIP 정보 탭: 건물 title, 설명, 소유주 이름
 * ZIP 풀기 탭: 입주자 목록
 * 클립보드 GLB 위에 문서 스타일로 표시
 *
 * 활성화 조건: BottomRight ZipperSlider progress가 0일 때
 * TanStack Query로 입주자 데이터 실시간 동기화
 */
export function TopRightOverlay({
  buildingId,
  buildingData,
  onEvict,
}: TopRightOverlayProps) {
  const zipperProgress = useBuildingDetailStore((s) => s.zipperProgress);

  // TanStack Query로 입주자 데이터 조회 (실시간 동기화)
  const { data: tenants = [] } = useTenantQuery(buildingId ?? null);

  // 텍스트 데이터 구성
  const infoTexts = useMemo(() => {
    if (!buildingData) return [];
    const texts: Array<{ label: string; value: string }> = [];

    if (buildingData.title) {
      texts.push({ label: "건물명", value: buildingData.title });
    }

    if (buildingData.ownerName) {
      texts.push({ label: "소유주", value: buildingData.ownerName });
    }

    if (buildingData.description) {
      texts.push({ label: "설명", value: buildingData.description });
    }

    return texts;
  }, [buildingData]);

  // TanStack Query 데이터로 입주자 텍스트 구성
  const tenantTexts = useMemo(() => {
    if (tenants.length === 0) {
      return [{ name: "입주자 없음", content: undefined }];
    }

    return tenants.slice(0, 8).map((tenant, index) => ({
      name: tenant.text || `입주자 ${index + 1}`,
      content: tenant.username,
    }));
  }, [tenants]);

  // visible prop을 그대로 사용 (부모에서 이미 조건 포함)
  // isZipperOpen은 내부 UI 상태용으로만 사용
  const isZipperOpen = zipperProgress <= 0.05;

  // 스크롤 ref와 핸들러 (CSS 3D transform 내에서 스크롤 작동을 위해 수동 처리)
  const paperRef = useRef<HTMLDivElement>(null);
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (paperRef.current) {
      paperRef.current.scrollTop += e.deltaY;
    }
  }, []);

  // 문서 스타일 (CSS로 보드 배경 표현)
  const documentStyles = {
    wrapper: {
      position: "relative" as const,
      paddingTop: "30px", // 클립 공간
      transform: "translateY(50%)",
    },
    container: {
      width: "220px",
      padding: "20px 16px",
      background: "linear-gradient(180deg, #d4c4a8 0%, #c9b896 100%)",
      borderRadius: "4px",
      fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
      color: "#2a2a2a",
      boxShadow: `
        0 4px 8px rgba(0,0,0,0.3),
        inset 0 1px 0 rgba(255,255,255,0.3),
        inset 0 -2px 0 rgba(0,0,0,0.1)
      `,
      border: "2px solid #8b7355",
    },
    // 빈티지 종이 효과
    paper: {
      background: "linear-gradient(180deg, #faf8f0 0%, #f5f0e1 100%)",
      maxHeight: "400px",
      padding: "16px 12px",
      borderRadius: "3px",
      boxShadow: "inset 0 0 10px rgba(0,0,0,0.05)",
      border: "1px solid #d4c9b0",
      overflowY: "scroll" as const,
      overflowX: "hidden" as const,
      pointerEvents: "auto" as const,
    },
    header: {
      fontSize: "16px",
      fontWeight: 700,
      marginBottom: "16px",
      paddingBottom: "10px",
      borderBottom: "2px solid #8b7355",
      fontFamily: "'Noto Serif KR', 'Georgia', serif",
      textAlign: "center" as const,
      letterSpacing: "2px",
      color: "#3d3d3d",
    },
    label: {
      color: "#666655",
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "uppercase" as const,
      letterSpacing: "1px",
      marginBottom: "2px",
    },
    value: {
      color: "#1a1a1a",
      fontSize: "13px",
      lineHeight: 1.5,
      marginBottom: "12px",
    },
    tenantItem: {
      padding: "8px 0",
      borderBottom: "1px dashed #c4b9a0",
    },
    tenantNumber: {
      color: "#8b7355",
      fontWeight: 700,
      fontSize: "12px",
    },
    tenantName: {
      color: "#2a2a2a",
      fontSize: "13px",
      fontWeight: 500,
    },
    tenantContent: {
      color: "#666655",
      fontSize: "10px",
      marginTop: "3px",
      // fontStyle: "italic" as const,
      paddingLeft: "16px",
    },
    emptyText: {
      color: "#888877",
      textAlign: "center" as const,
      fontSize: "12px",
      fontStyle: "italic" as const,
      padding: "20px 0",
    },
  };

  return (
    <OverlayCanvas corner="topRight" zoom={50}>
      {/* 지퍼가 열려있을 때만 콘텐츠 표시 */}
      {isZipperOpen && (
        <group
          position={[2.0, 3.0, 0]}
          rotation={[-Math.PI / 2, 0, Math.PI / 2]}
          onClick={(e) => console.log("3", e)}
        >
          {/* 클립 3D 모델 - HTML 상단에 배치 */}
          <group
            position={[0, 2.5, 0]}
            scale={[2.0, 2.0, 2.0]}
            rotation={[Math.PI / 2, 0, 0]}
          >
            <ClipModel />
          </group>

          {/* HTML 콘텐츠 */}
          <Html
            transform
            center
            occlude={"blending"}
            position={[0, 4, 0]}
            style={{ pointerEvents: "auto" }}
          >
            <div
              style={documentStyles.wrapper}
              onClick={(e) => console.log("1", e)}
            >
              {/* 보드 (배경) */}
              <div style={documentStyles.container}>
                {/* 종이 */}
                <div
                  ref={paperRef}
                  style={documentStyles.paper}
                  onWheel={handleWheel}
                >
                  {/* 헤더 */}
                  <h3 style={documentStyles.header}>건물 정보</h3>
                  {/* 건물 정보 (항상 표시) */}
                  {infoTexts.length > 0 && (
                    <div
                      style={{
                        marginBottom: "16px",
                        paddingBottom: "12px",
                        borderBottom: "1px solid #c4b9a0",
                      }}
                    >
                      {infoTexts.map((item, index) => (
                        <div key={index}>
                          <div style={documentStyles.label}>{item.label}</div>
                          <div style={documentStyles.value}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 헤더 */}
                  <h3 style={documentStyles.header}> 입주자 명부</h3>

                  {/* 입주자 목록 */}
                  <div>
                    <div
                      style={{ ...documentStyles.label, marginBottom: "8px" }}
                    >
                      입주자
                    </div>
                    {tenants.map((tenant, index) => (
                      <div
                        key={tenant.id || index}
                        style={documentStyles.tenantItem}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "baseline",
                            }}
                          >
                            <span style={documentStyles.tenantNumber}>
                              {index + 1}.
                            </span>
                            <span style={documentStyles.tenantName}>
                              {tenant.text || `입주자 ${index + 1}`}
                            </span>
                          </div>
                          {onEvict && tenant.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEvict(tenant);
                              }}
                              style={{
                                padding: "2px 6px",
                                fontSize: "10px",
                                background: "#8b7355",
                                color: "#fff",
                                border: "none",
                                borderRadius: "2px",
                                cursor: "pointer",
                                opacity: 0.8,
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = "1")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = "0.8")
                              }
                            >
                              퇴거
                            </button>
                          )}
                        </div>
                        {tenant.username && (
                          <div style={documentStyles.tenantContent}>
                            {tenant.username}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 데이터 없을 때 */}
                  {!buildingData && (
                    <div style={documentStyles.emptyText}>
                      건물 정보를 불러오는 중...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Html>
        </group>
      )}
    </OverlayCanvas>
  );
}

export default TopRightOverlay;
